import { Suspense, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useGqlQuery, useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useVehicleActions } from "@/app/Vehicles/Hooks/useVehicleActions";
import { Badge } from "@/components/catalyst/badge";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { mapVehicleModelToInput } from "@/app/Vehicles/Utils/mapVehicleModelToInput";
import { updateVehicleMutation } from "@/app/Vehicles/GraphQL/vehicleMutations";
import {
  UpdateVehicleInput,
  Currency,
  HireUnitType,
  VehicleType,
  ModelType,
} from "@/gql/graphql";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { VehicleTypeInput } from "@/app/Vehicles/Components/VehicleTypeInput";
import { FuelInput } from "@/app/Vehicles/Components/FuelInput";
import { TransmissionInput } from "@/app/Vehicles/Components/TransmissionInput";
import { VehicleAgeInput } from "@/app/Vehicles/Components/VehicleAgeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { HireUnitTypeInput } from "@/app/Common/Components/HireUnitType";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TrueFalseNullInput } from "@/lib/Components/Form/Inputs/TrueFalseNullInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useTranslations } from "use-intl";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useVehicleDrawer } from "@/app/Vehicles/Components/Drawer/useVehicleDrawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { SideTabs } from "@/app/Vehicles/Components/Drawer/VehicleDrawerTabs";
import { SortableVehicleImages } from "@/app/Vehicles/Components/SortableVehicleImages";
import { VehiclePurchasableExtraList } from "@/app/Vehicles/Components/VehiclePurchasableExtraList";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import { SupplierAgeRequirementForm } from "@/app/Suppliers/Forms/SupplierAgeRequirementForm";
export function VehicleDrawer() {
  const navigate = useNavigate();
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}",
  });
  const [activeTab, setActiveTab] = useState("details");

  const isOpen = !!search.vehicleId;

  const handleClose = () => {
    navigate({
      to: ".",
      search: (prev: any) => {
        return {
          ...prev,
          vehicleId: undefined,
        };
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative">
      <DialogBackdrop
        transition
        className="data-closed:opacity-0 fixed inset-0 bg-gray-900/50 transition-opacity duration-500 ease-in-out"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-6 right-6 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="data-closed:translate-x-full pointer-events-auto relative w-screen max-w-4xl transform transition duration-500 ease-in-out sm:duration-700"
            >
              <TransitionChild>
                <div className="data-closed:opacity-0 absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="relative rounded-md text-gray-400 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              </TransitionChild>

              {search.vehicleId ? (
                <div className="relative h-full overflow-hidden rounded-2xl bg-white shadow-xl">
                  {/* Main Content */}
                  <div className="flex h-full flex-col overflow-hidden">
                    <Suspense
                      fallback={
                        <div className="flex h-full items-center justify-center">
                          <Spinner />
                        </div>
                      }
                    >
                      {/* Header */}
                      <DialogHeading
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />

                      {/* Content */}
                      <div className="flex-1 overflow-y-auto px-6 py-6">
                        <TabContent activeTab={activeTab} />
                      </div>
                    </Suspense>
                  </div>
                </div>
              ) : null}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function TabContent({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    case "details":
      return <VehicleDrawerDetailsScreen />;
    case "images":
      return <VehicleDrawerImagesScreen />;
    case "activity":
      return <VehicleDrawerActivityScreen />;
    case "age":
      return <VehicleDriverAgeTab />;
    case "extras":
      return <VehicleDrawerExtrasScreen />;
    default:
      throw new Error("Unknown Tab");
  }
}

function VehicleDrawerExtrasScreen() {
  const vehicle = useVehicleDrawer();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          Purchasable extras
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Toggle which purchasable extras are available on this vehicle.
        </p>
      </div>
      <VehiclePurchasableExtraList vehicleId={vehicle.id} />
    </div>
  );
}

function VehicleDriverAgeTab() {
  const vehicle = useVehicleDrawer();
  const { mutateAsync } = useGqlMutation(updateVehicleMutation);

  return (
    <div>
      <RecordScreenFormCard<UpdateVehicleInput>
        title="Driver Age Requirement"
        initialValues={{
          minimum_age: vehicle.minimum_age,
          minimum_no_surcharge_age: vehicle.minimum_no_surcharge_age,
          maximum_age: vehicle.maximum_age,
          maximum_no_surcharge_age: vehicle.maximum_no_surcharge_age,
          age_surcharge_amount: vehicle.age_surcharge_amount,
          has_age_surcharge: vehicle.has_age_surcharge,
          age_surcharge_type: vehicle.age_surcharge_type,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: vehicle.id,
            input: values,
          });
        }}
      >
        <SupplierAgeRequirementForm currency={vehicle.supplier.currency} />
      </RecordScreenFormCard>
    </div>
  );
}

function DialogHeading({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  const vehicle = useVehicleDrawer();
  return (
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          {vehicle.heroImage ? (
            <img
              src={vehicle.heroImage}
              alt={vehicle.name}
              className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : null}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {vehicle.name}
            </h2>
            {vehicle.archived_at ? (
              <Badge color="purple">archived</Badge>
            ) : null}
          </div>
        </div>

        {/* Action buttons */}
        <VehicleDrawerActions vehicle={vehicle} />
      </div>

      {/* Tabs */}
      <div className="px-6">
        <SideTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
}

// Drawer-specific screen components that use the drawer context
function VehicleDrawerDetailsScreen() {
  const vehicle = useVehicleDrawer();
  const { mutateAsync } = useGqlMutation(updateVehicleMutation);
  const initialValues = mapVehicleModelToInput(vehicle);

  async function handleSubmit(values: UpdateVehicleInput) {
    await mutateAsync({
      id: vehicle.id,
      input: values,
    });
  }

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <RecordScreenFormCard<UpdateVehicleInput>
        title="Basic Information"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VehicleBasicForm />
      </RecordScreenFormCard>

      {/* Specifications */}
      <RecordScreenFormCard<UpdateVehicleInput>
        title="Specifications"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VehicleSpecificationsForm />
      </RecordScreenFormCard>

      {/* Rates & Pricing */}
      <RecordScreenFormCard<UpdateVehicleInput>
        title="Rates & Pricing"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VehicleRatesForm />
      </RecordScreenFormCard>

      {/* Features */}
      <RecordScreenFormCard<UpdateVehicleInput>
        title="Features"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VehicleFeaturesForm />
      </RecordScreenFormCard>
    </div>
  );
}

function VehicleDrawerImagesScreen() {
  const vehicle = useVehicleDrawer();
  return (
    <div className="">
      <SortableVehicleImages record={vehicle} />
    </div>
  );
}

function VehicleDrawerActivityScreen() {
  const vehicle = useVehicleDrawer();
  return (
    <div className="">
      <ActivityLogFeed modelId={vehicle.id} modelType={ModelType.Vehicle} />
    </div>
  );
}

function VehicleDrawerActions({ vehicle }: { vehicle: any }) {
  const getActions = useVehicleActions();

  if (!vehicle) {
    return null;
  }

  const actionGroups = getActions(vehicle);

  return <ActionGroupButtons buttons={actionGroups} />;
}

function VehicleBasicForm() {
  return (
    <>
      <TextInput
        name="name"
        label="Name"
        tooltip="The name the customer will see"
      />
      <TextInput name="brand" label="Brand" optionalLabel />
      <TextInput name="model" label="Model" optionalLabel />
      <TextInput
        name="code"
        label="Code"
        optionalLabel
        tooltip="Internal reference for the vehicle"
      />
      <div className="col-span-full">
        <RichTextInput
          optionalLabel
          name="description"
          label="Description"
          className="col-span-full"
        />
      </div>
    </>
  );
}

function VehicleSpecificationsForm() {
  const { values } = useFormState<UpdateVehicleInput>();
  const isCaravan = values.type === VehicleType.Caravan;

  return (
    <>
      <VehicleTypeInput name="type" label="Vehicle type" />
      {isCaravan ? null : (
        <>
          <FuelInput name="fuel" label="Fuel" />
          <TransmissionInput name="transmission" label="Transmission" />
          <VehicleAgeInput
            optionalLabel
            name="approximate_age"
            label="Vehicle age"
          />
          <NumberInput
            name="seatbelts"
            label="Seatbelts"
            inputProps={{ min: 0 }}
          />
          <NumberInput
            name="child_seat_anchor_points"
            label="Child seat anchors"
            optionalLabel
            inputProps={{ min: 0 }}
          />
        </>
      )}
    </>
  );
}

function VehicleRatesForm() {
  const { values, setFieldValue } = useFormState<UpdateVehicleInput>();
  const { isAdmin } = useTenant();
  const t = useTranslations();

  const { data } = useGqlQuery(
    supplierRecordQuery,
    { id: values.supplier?.connect! },
    { enabled: !!values.supplier?.connect },
  );

  const currency = data?.supplier.currency ?? Currency.Usd;

  return (
    <>
      <HireUnitTypeInput name="hire_unit_type" label="Charged per" />
      <div className="col-span-9" />
      {isAdmin && (
        <CurrencyInput
          name="hire_unit_rate"
          label="Relocation rate"
          currency={currency}
        />
      )}

      <CurrencyInput
        isEditable={false}
        name="extra_hire_unit_rate"
        currency={currency}
        label={`Extra ${t(
          `relocation.hire_unit_type.${values.hire_unit_type ?? HireUnitType.Day}`,
        )} rate`}
      />
      <CurrencyInput
        currency={currency}
        onChange={(newValue) => {
          if (newValue === null) return;
          setFieldValue(
            "extra_hire_unit_rate",
            newValue + (values?.extra_hire_unit_deposit_rate ?? 0),
          );
        }}
        name="extra_hire_unit_supplier_net_rate"
        label="Supplier net"
      />
      <CurrencyInput
        currency={currency}
        isEditable={isAdmin}
        name="extra_hire_unit_deposit_rate"
        onChange={(value) => {
          setFieldValue(
            "extra_hire_unit_rate",
            (value ?? 0) + (values?.extra_hire_unit_supplier_net_rate ?? 0),
          );
        }}
        label="Imoova net"
      />
    </>
  );
}

function VehicleFeaturesForm() {
  const { values } = useFormState<UpdateVehicleInput>();
  const { data } = useGqlQuery(
    supplierRecordQuery,
    { id: values.supplier?.connect! },
    { enabled: !!values.supplier?.connect },
  );

  const currency = data?.supplier.currency ?? Currency.Usd;
  const isSleeper = [
    VehicleType.FourWheelDriveCamper,
    VehicleType.CamperVan,
    VehicleType.MotorHome,
    VehicleType.Caravan,
  ].includes(values.type!);

  return (
    <>
      {isSleeper && (
        <>
          <NumberInput name="sleeps" label="Sleeps" inputProps={{ min: 0 }} />
          <TrueFalseNullInput label="Self contained" name="is_self_contained" />
          <TrueFalseNullInput label="Kitchen" name="has_kitchen" />
          <TrueFalseNullInput
            label="Shower & toilet"
            name="has_shower_and_toilet"
          />
          <TrueFalseNullInput label="Heating" name="has_heating" />
        </>
      )}

      <TrueFalseNullInput
        name="is_pet_friendly"
        label="Pet friendly"
        optionalLabel
      />

      {values.is_pet_friendly && (
        <CurrencyInput
          name="pet_cleaning_fee"
          label="Pet cleaning fee"
          currency={currency}
          optionalLabel
        />
      )}
    </>
  );
}
